// Standalone localStorage prefs for Personal Life OS UI settings.
// NOT synced to LifeOsState — device-local only (like browser preferences).
// Quota-safe: all reads/writes are wrapped in try/catch.
// D35: also stores the active focus timer session for cross-reload resume.
// D37: backup reminder helpers (last-backup timestamp + enabled flag).

const CAPACITY_KEY = "personal-life-os:day-capacity";

export const DEFAULT_DAY_CAPACITY_MINUTES = 480; // 8 hours
export const EXTENDED_DAY_CAPACITY_MINUTES = 600; // 10 hours

/**
 * Read the user's preferred day capacity in minutes from localStorage.
 * Falls back to DEFAULT_DAY_CAPACITY_MINUTES (480) when unset or on any error.
 * Clamped: only 480 or 600 are valid stored values; any other positive number is
 * accepted as-is (future-proof), non-positive falls back to default.
 */
export function getDayCapacityMinutes(): number {
  try {
    const raw = localStorage.getItem(CAPACITY_KEY);
    if (raw === null) return DEFAULT_DAY_CAPACITY_MINUTES;
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_DAY_CAPACITY_MINUTES;
    return parsed;
  } catch {
    return DEFAULT_DAY_CAPACITY_MINUTES;
  }
}

/**
 * Persist the preferred day capacity in minutes.
 * Silently swallows quota / private-browsing errors.
 */
export function setDayCapacityMinutes(minutes: number): void {
  try {
    localStorage.setItem(CAPACITY_KEY, String(minutes));
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// Onboarding dismissal flag (P2-12)
// ---------------------------------------------------------------------------

const ONBOARDING_KEY = "personal-life-os:onboarding-done";

/**
 * Returns true once the user has dismissed the first-run onboarding modal.
 * Falls back to false on any localStorage error (shows the modal again — safe).
 */
export function isOnboardingDone(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Mark onboarding as complete so the modal never reshows.
 * Silently swallows quota / private-browsing errors.
 */
export function setOnboardingDone(): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, "1");
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// What's New version-aware seen flag (A8)
// ---------------------------------------------------------------------------

const LAST_SEEN_VERSION_KEY = "personal-life-os:last-seen-version";

/**
 * Returns the last app version the user has seen the What's New modal for,
 * or null if never shown. Falls back to null on any localStorage error.
 */
export function getLastSeenVersion(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_VERSION_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the version string after the user dismisses the What's New modal.
 * Silently swallows quota / private-browsing errors.
 */
export function setLastSeenVersion(v: string): void {
  try {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, v);
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// Getting-started checklist dismissal flag (A1)
// ---------------------------------------------------------------------------

const GETTING_STARTED_KEY = "personal-life-os:getting-started-dismissed";

/**
 * Returns true when the user has explicitly dismissed the getting-started checklist.
 * Falls back to false on any localStorage error (show the checklist — safe).
 */
export function isGettingStartedDismissed(): boolean {
  try {
    return localStorage.getItem(GETTING_STARTED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Persist the dismissal flag so the checklist never reshows.
 * Silently swallows quota / private-browsing errors.
 */
export function setGettingStartedDismissed(): void {
  try {
    localStorage.setItem(GETTING_STARTED_KEY, "1");
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// Monthly-close auto prompt (D47)
// closeDay: ngày trong tháng bắt đầu nhắc chốt tháng trước (mặc định 1).
// promptDismissed: monthKey người dùng đã "Để sau" → không auto-bật popup nữa.
// ---------------------------------------------------------------------------

const MONTHLY_CLOSE_DAY_KEY = "personal-life-os:monthly-close-day";
const MONTHLY_CLOSE_DISMISSED_KEY = "personal-life-os:monthly-close-dismissed";

export const DEFAULT_MONTHLY_CLOSE_DAY = 1;

/** Ngày chốt tháng (1–28). Fallback 1 khi chưa đặt / lỗi / ngoài khoảng. */
export function getMonthlyCloseDay(): number {
  try {
    const raw = localStorage.getItem(MONTHLY_CLOSE_DAY_KEY);
    if (raw === null) return DEFAULT_MONTHLY_CLOSE_DAY;
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 28) return DEFAULT_MONTHLY_CLOSE_DAY;
    return parsed;
  } catch {
    return DEFAULT_MONTHLY_CLOSE_DAY;
  }
}

export function setMonthlyCloseDay(day: number): void {
  try {
    localStorage.setItem(MONTHLY_CLOSE_DAY_KEY, String(Math.min(28, Math.max(1, Math.round(day)))));
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

/** monthKey người dùng đã bấm "Để sau" (chỉ tắt auto-popup, chip vẫn hiện). */
export function getMonthlyCloseDismissed(): string | null {
  try {
    return localStorage.getItem(MONTHLY_CLOSE_DISMISSED_KEY);
  } catch {
    return null;
  }
}

export function setMonthlyCloseDismissed(monthKey: string): void {
  try {
    localStorage.setItem(MONTHLY_CLOSE_DISMISSED_KEY, monthKey);
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// Focus timer session persistence (D35)
// Device-local only — NOT synced.
// ---------------------------------------------------------------------------

const FOCUS_TIMER_KEY = "personal-life-os:focus-timer";

export interface FocusTimerSession {
  /** ID of the task being focused on. */
  taskId: string;
  /** Display title for the task. */
  title: string;
  /** Total planned duration in minutes. */
  durationMin: number;
  /** ms timestamp when the timer was started (or last resumed). */
  startedAt: number;
  /** Total accumulated pause time in ms. */
  pausedAccum: number;
  /** ms timestamp when the timer was last paused; absent = currently running. */
  pausedAt?: number;
}

/**
 * Read the active focus timer session from localStorage.
 * Returns null when no session is stored or on any parse/quota error.
 */
export function getFocusTimerSession(): FocusTimerSession | null {
  try {
    const raw = localStorage.getItem(FOCUS_TIMER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as FocusTimerSession;
  } catch {
    return null;
  }
}

/**
 * Persist the active focus timer session to localStorage.
 * Silently swallows quota / private-browsing errors.
 */
export function setFocusTimerSession(session: FocusTimerSession): void {
  try {
    localStorage.setItem(FOCUS_TIMER_KEY, JSON.stringify(session));
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

/**
 * Clear the focus timer session (stop/done/expiry).
 * Silently swallows errors.
 */
export function clearFocusTimerSession(): void {
  try {
    localStorage.removeItem(FOCUS_TIMER_KEY);
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// Backup reminder (D37)
// Tracks when the user last exported a backup; drives the BackupReminder banner.
// ---------------------------------------------------------------------------

const LAST_BACKUP_KEY = "personal-life-os:last-backup-at";
const BACKUP_REMINDER_KEY = "personal-life-os:backup-reminder";

/**
 * Returns the ISO timestamp string of the last manual backup export,
 * or null if the user has never exported. Falls back to null on any error.
 */
export function getLastBackupAt(): string | null {
  try {
    return localStorage.getItem(LAST_BACKUP_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the current timestamp as the last backup time.
 * Call this immediately after a successful export download.
 * Silently swallows quota / private-browsing errors.
 */
export function setLastBackupAtNow(): void {
  try {
    localStorage.setItem(LAST_BACKUP_KEY, new Date().toISOString());
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

/**
 * Returns true (default) when the weekly backup reminder banner is enabled.
 * Falls back to true on any error so the reminder is shown by default.
 */
export function isBackupReminderEnabled(): boolean {
  try {
    const raw = localStorage.getItem(BACKUP_REMINDER_KEY);
    // Explicitly disabled only when stored as "0".
    if (raw === "0") return false;
    return true;
  } catch {
    return true;
  }
}

/**
 * Enable or disable the weekly backup reminder banner.
 * Silently swallows quota / private-browsing errors.
 */
export function setBackupReminderEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(BACKUP_REMINDER_KEY, enabled ? "1" : "0");
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

const BACKUP_SNOOZE_KEY = "personal-life-os:backup-snooze-until";

/** Epoch ms until which the backup banner stays hidden (0 = not snoozed). */
export function getBackupSnoozedUntil(): number {
  try {
    return Number(localStorage.getItem(BACKUP_SNOOZE_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Hide the backup banner for `days` (persists across reloads, unlike session dismiss). */
export function snoozeBackupReminder(days = 3): void {
  try {
    localStorage.setItem(BACKUP_SNOOZE_KEY, String(Date.now() + days * 86_400_000));
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// D54: Planning preference — free-text personal criteria fed into the AI MIT
// suggestion prompt (device-local; e.g. "Ưu tiên deep work buổi sáng, né họp").
// ---------------------------------------------------------------------------

const PLANNING_PREF_KEY = "personal-life-os:planning-preference";
const PLANNING_PREF_MAX = 400; // keep prompt small

/** User's custom MIT-selection criteria (empty string = none). */
export function getPlanningPreference(): string {
  try {
    return localStorage.getItem(PLANNING_PREF_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setPlanningPreference(text: string): void {
  try {
    localStorage.setItem(PLANNING_PREF_KEY, text.slice(0, PLANNING_PREF_MAX));
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// UI Layout Theme Choice — "classic" (Bento) vs "aura" (Glassmorphism Redesign)
// ---------------------------------------------------------------------------

const UI_THEME_KEY = "personal-life-os:ui-theme";

export function getUiTheme(): "classic" | "aura" {
  try {
    const raw = localStorage.getItem(UI_THEME_KEY);
    if (raw === "aura") return "aura";
    return "classic";
  } catch {
    return "classic";
  }
}

export function setUiTheme(theme: "classic" | "aura"): void {
  try {
    localStorage.setItem(UI_THEME_KEY, theme);
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ---------------------------------------------------------------------------
// Wipe-on-sign-out (shared-device privacy). Default OFF — signing out keeps the
// uid-keyed local cache for fast re-login. When ON, sign-out clears this device's
// local copy (cloud stays source of truth → data returns on next login).
// ---------------------------------------------------------------------------

const WIPE_ON_SIGNOUT_KEY = "personal-life-os:wipe-on-signout";

export function isWipeOnSignOutEnabled(): boolean {
  try {
    return localStorage.getItem(WIPE_ON_SIGNOUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function setWipeOnSignOutEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(WIPE_ON_SIGNOUT_KEY, enabled ? "1" : "0");
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Last sync timestamps — persisted so the diagnostics panel survives reload
// (otherwise "Đẩy/Kéo gần nhất" resets to "—" on every page load).
// ---------------------------------------------------------------------------

const SYNC_PUSH_KEY = "personal-life-os:sync-last-push";
const SYNC_PULL_KEY = "personal-life-os:sync-last-pull";

export function getSyncPushAt(): string | null {
  try { return localStorage.getItem(SYNC_PUSH_KEY); } catch { return null; }
}
export function setSyncPushAt(iso: string): void {
  try { localStorage.setItem(SYNC_PUSH_KEY, iso); } catch { /* ignore */ }
}
export function getSyncPullAt(): string | null {
  try { return localStorage.getItem(SYNC_PULL_KEY); } catch { return null; }
}
export function setSyncPullAt(iso: string): void {
  try { localStorage.setItem(SYNC_PULL_KEY, iso); } catch { /* ignore */ }
}

